import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Phone, Clock, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "@/lib/api";
import { DARK_INPUT, errMsg, fmtDate } from "../control/shared";

function Row({ w, onSaved, onOpen }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/admin/worker-mgmt/${w.user_id}/card/followup`, { note }); toast.success("Follow-up logged"); setNote(""); onSaved(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const last = w.followups[w.followups.length - 1];
  return (
    <div className="py-3 border-b border-slate-800/60 grid md:grid-cols-[1.3fr_1fr_1.6fr] gap-3 items-center" data-testid={`uc-row-${w.user_id}`}>
      <div className="min-w-0">
        <button onClick={() => onOpen(w.user_id)} className="font-semibold hover:text-orange-300 text-left" data-testid={`uc-open-${w.user_id}`}>{w.name}</button>
        <div className="text-xs text-slate-500 font-mono">{w.worker_code} · {w.city}</div>
      </div>
      <div className="text-xs">
        <div className="text-amber-300 font-semibold flex items-center gap-1" data-testid={`uc-days-${w.user_id}`}><Clock size={12}/>{w.days_waiting} days waiting</div>
        <div className="text-slate-500">Printed {fmtDate(w.printed_at)} · {w.reminders_sent} reminder{w.reminders_sent === 1 ? "" : "s"}</div>
        {last && <div className="text-slate-400 mt-1 truncate" title={last.note}>Last call: {last.note} ({fmtDate(last.at)})</div>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <a href={`tel:${w.phone}`} className="inline-flex items-center gap-1 rounded-md bg-orange-500 hover:bg-orange-600 px-3 h-8 text-xs font-semibold text-white" data-testid={`uc-call-${w.user_id}`}><Phone size={12}/>{w.phone}</a>
        {w.emergency_contact && <a href={`tel:${w.emergency_contact}`} className="text-xs text-slate-400 underline" data-testid={`uc-call-emergency-${w.user_id}`}>Emergency</a>}
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Call note, e.g. will collect Friday" className={`${DARK_INPUT} h-8 text-xs flex-1 min-w-[140px]`} data-testid={`uc-note-${w.user_id}`}/>
        <Button size="sm" className="h-8 bg-slate-700 hover:bg-slate-600" disabled={note.trim().length < 3 || busy} onClick={save} data-testid={`uc-log-${w.user_id}`}>Log</Button>
      </div>
    </div>
  );
}

export function UncollectedCards({ onOpen, refreshKey }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(true);
  const load = useCallback(() => api.get("/admin/worker-mgmt/uncollected", { params: { days: 7 } }).then((r) => setD(r.data)).catch(() => setD({ items: [], total: 0 })), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!d) return null;
  return (
    <div className="admin-card p-4 mt-3" data-testid="uncollected-cards">
      <button className="w-full flex items-center justify-between" onClick={() => setOpen(!open)} data-testid="uc-toggle">
        <span className="font-bold text-sm">Not collected after 7 days <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${d.total ? "bg-amber-500/20 text-amber-300" : "bg-slate-800 text-slate-400"}`} data-testid="uc-count">{d.total}</span></span>
        {open ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
      </button>
      {open && (d.items.length
        ? <div className="mt-2">{d.items.map((w) => <Row key={w.user_id} w={w} onSaved={load} onOpen={onOpen}/>)}</div>
        : <p className="text-xs text-slate-500 mt-2" data-testid="uc-empty">Every printed card has been collected within 7 days.</p>)}
    </div>
  );
}
